import type { ComponentTree } from '../types/component-tree.js'
import * as yaml from 'js-yaml'

export const TAILWIND_INSTRUCTIONS = `HINTS (PRIMARY GUIDANCE):
- Each component may have a 'hints' field listing design tokens and variables from Figma
- Hints indicate which Figma variables/styles were applied (e.g., colors, spacing, text styles)
- Use hints as your PRIMARY guide for selecting Tailwind classes and design decisions
- Example: hint "colors/primary" with type "COLOR" → use your project's primary color token/class
- Example: hint "spacing/md" with type "FLOAT" → use corresponding spacing scale (e.g., space-4)
- If hints are present, prefer them over direct style values for better design system consistency
- If hints are ambiguous or unclear, ask the user for clarification

LAYOUT TO TAILWIND:
- mode: 'horizontal' → flex flex-row
- mode: 'vertical' → flex flex-col
- align.main → justify-* (start, center, end, between)
- align.cross → items-* (start, center, end, stretch)

SIZING:
- sizingIntent: "fixed" → use nearest Tailwind class or pixel value if not applying Tailwind classes
- sizingIntent: "flexible" → use flex-1, ignore pixel value
- sizingIntent: "hug" → let content determine size

STYLES (SECONDARY GUIDANCE):
- Use style properties when hints are not available or insufficient
- Background colors, borders, shadows, etc. are provided as fallback values
- When hints and styles conflict, prefer hints for design system consistency

ICONS:
- Use lucide-react for all icons
- Map Figma icon names to closest Lucide equivalent

STRUCTURE:
- Frames with meta.structural: true can be flattened
- child.alignSelf: "stretch" → w-full or self-stretch
- child.grow: 1 → flex-1`

/**
 * Generate formatted prompt text from ComponentTree
 * Mirrors backend logic from figma.routes.ts
 *
 * @param componentTree - Parsed component tree from transformClipboard
 * @param fileName - Name of the Figma file (optional)
 * @param nodeName - Name of the selected node (optional)
 * @param includeImage - Whether an image is provided as visual reference (optional, default false)
 * @param instructions - Optional instructions block to insert before COMPONENT TREE (null/omit = no instructions)
 * @returns Formatted prompt text for Claude
 */
export function componentTreeToPrompt(
  componentTree: ComponentTree,
  fileName = 'Figma Design',
  nodeName?: string,
  includeImage = false,
  instructions?: string | null,
): string {
  // Serialize ComponentTree to YAML (matching backend implementation)
  const yamlContext = yaml.dump(componentTree, {
    indent: 2,
    lineWidth: 120,
    noRefs: true,
    sortKeys: false, // Preserve field order
  })

  const title = nodeName ? `${fileName} - ${nodeName}` : fileName

  const header = `Figma Design: ${title}`
  const imageNote = includeImage
    ? `\n- An image is provided as a visual reference\n- Use the component tree as the primary source of truth`
    : ''
  const instructionBlock = instructions
    ? `\n\nINSTRUCTIONS:\n- Reproduce this Figma component using the structured component tree below\n- The YAML contains complete layout, style, and content specifications\n- Prioritize hints for design decisions, use component styles when hints are insufficient, and ask for clarification when ambiguous${imageNote}\n\n${instructions}`
    : ''

  return `${header}${instructionBlock}\n\nCOMPONENT TREE:\n${yamlContext}`
}
